import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Clock, ArrowRight } from "lucide-react";
import { MaskLines, Reveal } from "@/components/Reveal";
import { api } from "@/lib/api";

const STATUS = {
  open: { label: "Plätze frei", cls: "text-plantain border-plantain" },
  soldout: { label: "Ausverkauft", cls: "text-harissa border-harissa" },
  past: { label: "Vorbei", cls: "text-muted border-line" },
};

function formatDate(d) {
  try {
    return new Date(d).toLocaleDateString("de-CH", { weekday: "short", day: "2-digit", month: "long" });
  } catch { return d; }
}

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/events")
      .then((r) => setEvents(r.data))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="events-page" className="pt-28 sm:pt-36 min-h-screen">
      <header className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-harissa mb-5">Pop-Up Termine</p>
        <h1 className="font-display uppercase text-[16vw] sm:text-[13vw] leading-[0.82] tracking-tight">
          <MaskLines lines={["Wo & wann", <span key="s" className="text-stroke">wir sind.</span>]} />
        </h1>
        <p className="font-sans text-lg text-muted mt-6 max-w-xl">
          Wir tauchen dort auf, wo der Abend passiert. Kommende Termine, laufend aktualisiert.
        </p>
      </header>

      <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-20 sm:py-28">
        {loading ? (
          <p className="font-mono text-muted" data-testid="events-loading">Lade Termine…</p>
        ) : events.length === 0 ? (
          <p className="font-mono text-muted" data-testid="events-empty">Momentan keine Termine geplant. Schau bald wieder vorbei.</p>
        ) : (
          <div className="border-t border-line" data-testid="events-list">
            {events.map((e, i) => {
              const s = STATUS[e.status] || STATUS.open;
              return (
                <Reveal key={e.id} delay={i * 0.05}>
                  <div className={`grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 items-center border-b border-line py-8 px-1 sm:px-4 group hover:bg-surface transition-colors duration-300 ${e.status === "past" ? "opacity-50" : ""}`} data-testid={`event-${i}`}>
                    <div className="md:col-span-3">
                      <span className="font-mono text-sm uppercase tracking-widest text-mango">{formatDate(e.date)}</span>
                      <p className="font-mono text-xs text-muted mt-1 flex items-center gap-1"><Clock size={12} /> {e.time}</p>
                    </div>
                    <div className="md:col-span-5">
                      <h3 className="font-head font-[800] text-2xl sm:text-3xl uppercase leading-none">{e.title}</h3>
                      <p className="font-mono text-xs text-muted mt-2 flex items-center gap-1"><MapPin size={12} /> {e.venue} · {e.city}</p>
                      {e.note && <p className="font-sans text-sm text-muted mt-2">{e.note}</p>}
                    </div>
                    <div className="md:col-span-4 md:text-right">
                      <span className={`inline-block font-mono text-[11px] uppercase tracking-widest border px-3 py-1 ${s.cls}`}>{s.label}</span>
                      {e.status === "open" && (
                        <Link to="/reservierung" className="block md:inline-block md:ml-4 mt-3 md:mt-0 font-mono text-xs uppercase tracking-widest text-ink group-hover:text-harissa transition-colors" data-testid={`event-reserve-${i}`}>
                          Anfragen →
                        </Link>
                      )}
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        )}

        <Reveal>
          <div className="mt-16 border border-line p-8 sm:p-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div>
              <h2 className="font-head font-[900] text-2xl sm:text-4xl uppercase">Privat-Event oder Kollab?</h2>
              <p className="font-sans text-muted mt-3 max-w-md">Wir bringen die Boxen auch zu dir. Schreib uns, wir finden was.</p>
            </div>
            <Link to="/kontakt" className="inline-flex items-center gap-2 bg-harissa text-base font-mono text-sm uppercase tracking-widest px-7 py-4 hover:bg-ink transition-colors whitespace-nowrap" data-testid="events-contact-btn">
              Anfragen <ArrowRight size={16} />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
